import { useMemo, useRef, useState } from "react";

import Button from "../Button/Button";
import CardContent from "../Card/Card";
import Card from "../Card/Card";
import { useToast } from "../../hooks/useToast";
import {
    BULK_SIGNUP_FORMAT_HINT,
    MAX_BULK_SIGNUP_ROWS,
    parseBulkSignupInput,
} from "../../../helpers/parseInviteList";
import { startBulkSignup } from "../../Reducers/actions/adminActions";
import "./Invite.scss";

const INPUT_MODES = {
    paste: "paste",
    csv: "csv",
};

export default function Invite() {
    const [inputMode, setInputMode] = useState(INPUT_MODES.paste);
    const [bulkInput, setBulkInput] = useState("");
    const [csvFileName, setCsvFileName] = useState("");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const fileInputRef = useRef(null);
    const toast = useToast();

    const parsed = useMemo(() => parseBulkSignupInput(bulkInput), [bulkInput]);
    const rowLimitExceeded = parsed.entries.length > MAX_BULK_SIGNUP_ROWS;

    const clearCsvSelection = ({ clearInput = true, clearResult = true } = {}) => {
        if (clearInput) {
            setBulkInput("");
            setCsvFileName("");
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        }
        if (clearResult) {
            setResult(null);
        }
    };

    const handleCsvUpload = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (!file.name.toLowerCase().endsWith(".csv")) {
            toast("Please upload a .csv file", "warning");
            event.target.value = "";
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            setBulkInput(String(reader.result || ""));
            setCsvFileName(file.name);
            setResult(null);
        };
        reader.onerror = () => {
            toast("Could not read the CSV file", "danger");
        };
        reader.readAsText(file);
    };

    const submitBulkSignup = async () => {
        if (!parsed.ok) {
            toast(parsed.message || "Fix the input format before submitting", "warning");
            return;
        }

        if (parsed.entries.length === 0) {
            toast("Add at least one valid row", "warning");
            return;
        }

        if (rowLimitExceeded) {
            toast(`Maximum ${MAX_BULK_SIGNUP_ROWS} rows per bulk signup. Remove ${parsed.entries.length - MAX_BULK_SIGNUP_ROWS} row(s).`, "warning");
            return;
        }

        setLoading(true);
        setResult(null);

        try {
            const body =
                inputMode === INPUT_MODES.csv && bulkInput.trim()
                    ? { csv: bulkInput }
                    : { entries: parsed.entries };

            // The backend processes the whole batch and returns the summary + per-row
            // results in a single synchronous response (no job polling).
            const response = await startBulkSignup(body);
            if (!response?.success) {
                toast(response?.message || "Failed to create accounts", "danger");
                return;
            }

            setResult(response);

            const { summary } = response;
            const parts = [];
            if (summary?.created > 0) parts.push(`${summary.created} created`);
            if (summary?.skipped > 0) parts.push(`${summary.skipped} skipped`);
            if (summary?.invalid > 0) parts.push(`${summary.invalid} invalid`);
            if (summary?.failed > 0) parts.push(`${summary.failed} failed`);
            toast(
                parts.join(" | ") || "Bulk signup finished",
                summary?.created > 0 ? "success" : "warning",
            );

            if (inputMode === INPUT_MODES.paste) {
                setBulkInput("");
            } else {
                clearCsvSelection({ clearInput: true, clearResult: false });
            }
        } catch (error) {
            toast(
                error?.response?.message || error?.message || "Error creating accounts",
                "danger",
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card classNames="invite-card p-12">
            <CardContent>
                <h2 className="invite-card__title mb-4 mt-0">Bulk create accounts</h2>
                <p className="invite-card__subtitle mb-16 mt-0">{BULK_SIGNUP_FORMAT_HINT}</p>

                <div className="invite-mode-tabs mb-16" role="tablist" aria-label="Bulk signup input mode">
                    <button
                        type="button"
                        role="tab"
                        aria-selected={inputMode === INPUT_MODES.paste}
                        className={`invite-mode-tab${inputMode === INPUT_MODES.paste ? " is-active" : ""}`}
                        onClick={() => {
                            setInputMode(INPUT_MODES.paste);
                            setResult(null);
                        }}
                    >
                        Paste rows
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={inputMode === INPUT_MODES.csv}
                        className={`invite-mode-tab${inputMode === INPUT_MODES.csv ? " is-active" : ""}`}
                        onClick={() => {
                            setInputMode(INPUT_MODES.csv);
                            setResult(null);
                        }}
                    >
                        Upload CSV
                    </button>
                </div>

                {inputMode === INPUT_MODES.paste ? (
                    <textarea
                        className="invite-textarea w-full p-8 mb-8"
                        rows="8"
                        placeholder={"Full Name,Username,Role,Email\nCreator One,creator_one,creator,one@test.com\nAda Lovelace,ada_lovelace,creator,ada@test.com"}
                        value={bulkInput}
                        onChange={(event) => {
                            setBulkInput(event.target.value);
                            setResult(null);
                        }}
                    />
                ) : (
                    <div className="invite-csv-upload mb-8">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv,text/csv"
                            className="invite-csv-input"
                            onChange={handleCsvUpload}
                        />
                        {csvFileName ? (
                            <div className="invite-csv-meta">
                                <span>{csvFileName}</span>
                                <button
                                    type="button"
                                    className="invite-csv-clear"
                                    onClick={() => clearCsvSelection({ clearInput: true, clearJob: true })}
                                >
                                    Remove
                                </button>
                            </div>
                        ) : (
                            <p className="invite-csv-hint mt-0 mb-0">
                                CSV must include a header row. Example:{" "}
                                <code>Full Name,Username,Role,Email</code>
                            </p>
                        )}
                    </div>
                )}

                {!parsed.ok && bulkInput.trim() ? (
                    <p className="invite-preview invite-preview--error mb-8 mt-0">{parsed.message}</p>
                ) : null}

                <p className={`invite-preview mb-8 mt-0${rowLimitExceeded ? " invite-preview--error" : ""}`}>
                    {parsed.entries.length === 0
                        ? "No valid rows detected yet."
                        : `${parsed.entries.length} account${parsed.entries.length === 1 ? "" : "s"} ready (max ${MAX_BULK_SIGNUP_ROWS}).`}
                    {rowLimitExceeded
                        ? ` Remove ${parsed.entries.length - MAX_BULK_SIGNUP_ROWS} row(s) to continue.`
                        : null}
                </p>

                {parsed.skipped.length > 0 ? (
                    <div className="invite-skipped mb-16">
                        <p className="invite-skipped__title mt-0 mb-8">
                            {parsed.skipped.length} row{parsed.skipped.length === 1 ? "" : "s"} skipped during parsing
                        </p>
                        <ul className="invite-skipped__list mt-0 mb-0">
                            {parsed.skipped.slice(0, 8).map((row) => (
                                <li key={`skip-${row.rowNumber}-${row.reason}`}>
                                    Row {row.rowNumber}: {row.reason}
                                </li>
                            ))}
                            {parsed.skipped.length > 8 ? (
                                <li>…and {parsed.skipped.length - 8} more</li>
                            ) : null}
                        </ul>
                    </div>
                ) : null}

                <div className="d-flex justify-content-end">
                    <Button
                        onClick={submitBulkSignup}
                        disabled={parsed.entries.length === 0 || rowLimitExceeded || !parsed.ok || loading}
                        classNames="w-200"
                    >
                        {loading ? "Creating..." : "Create accounts"}
                    </Button>
                </div>

                {result ? (
                    <div className="invite-results mt-24">
                        <h3 className="invite-results__title mt-0 mb-8">Results</h3>
                        {result.summary ? (
                            <div className="invite-results__summary mb-12">
                                <span>{result.summary.created ?? 0} created</span>
                                <span>{result.summary.skipped ?? 0} skipped</span>
                                <span>{result.summary.failed ?? 0} failed</span>
                                <span>{result.summary.invalid ?? 0} invalid</span>
                            </div>
                        ) : null}
                        {Array.isArray(result.results) && result.results.length > 0 ? (
                            <div className="invite-results__table-wrap">
                                <table className="invite-results__table">
                                    <thead>
                                        <tr>
                                            <th>Row</th>
                                            <th>Email</th>
                                            <th>Status</th>
                                            <th>Message</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {result.results.map((row, index) => (
                                            <tr
                                                key={`${row.rowNumber || index}-${row.email || "unknown"}`}
                                                className={`status-${row.status}`}
                                            >
                                                <td>{row.rowNumber || index + 1}</td>
                                                <td>{row.email || "-"}</td>
                                                <td>{row.status}</td>
                                                <td>{row.message}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </CardContent>
        </Card>
    );
}
